/**
 * Écran de progression d'un déploiement ou d'une restauration (§3.3, §4.2).
 *
 * Le flux SSE est la source de vérité de l'affichage temps réel ; un appel à
 * `GET /api/deployments/:id` complète les métadonnées que le flux ne porte pas
 * (slug, domaine, durées par étape). Un rechargement de page ne perd rien : le
 * `snapshot` rejoue l'état complet, logs compris, y compris sur un job déjà
 * terminé.
 *
 * **Le type du job vient de la fiche du déploiement**, et non du flux : le
 * `snapshot` SSE (`SseSnapshotEvent`) ne transporte pas cette information — son
 * champ `type` nomme l'événement, pas la nature du job. `DeploymentSummaryDto`
 * la porte, elle, dans son champ `type`. Tant que la fiche n'est pas arrivée,
 * on suppose un `DEPLOY` : c'est le cas majoritaire, et le seul effet d'une
 * hypothèse erronée pendant ces quelques centaines de millisecondes serait une
 * barre à six segments qui se réduit ensuite à deux.
 */

import {
  canCancel,
  countRefinements,
  hasInitialDescription,
  isAwaitingUser,
  isTerminal,
  STATE_LABELS,
  type DeploymentQueueStatusDto,
} from '@portail/shared';
import { Ban, ExternalLink, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  cancelDeployment,
  getDeployment,
  getDeploymentLogs,
  retryDeployment,
  refineDeployment,
  reviewDeployment,
} from '../api';
import { FailurePanel } from '../components/FailurePanel';
import { FunctionalChangesPanel } from '../components/FunctionalChangesPanel';
import { LogPanel } from '../components/LogPanel';
import { ProgressBar } from '../components/ProgressBar';
import { SuccessPanel } from '../components/SuccessPanel';
import { Button, Card, ErrorBanner, PageHeader, Skeleton } from '../components/ui';
import { useAnimatedProgress } from '../hooks/useAnimatedProgress';
import { messageOf, useAsync } from '../hooks/useAsync';
import { useDeploymentStream } from '../hooks/useDeploymentStream';
import { useSession } from '../hooks/useSession';
import { useTicker } from '../hooks/useTicker';
import { formatDateTime, formatDuration, formatElapsed } from '../lib/format';
import { MAX_LOG_LINES, type LogEntry } from '../lib/logs';

/**
 * Taille de page des logs persistés. L'API plafonne `pageSize` à 200 : pour
 * remonter davantage de lignes, il faut enchaîner les pages.
 */
const LOG_PAGE_SIZE = 200;

/**
 * Cadence de rafraîchissement de la fiche pendant l'attente en file.
 *
 * Un job en attente n'émet aucun événement : sans ce rappel, la position dans la
 * file resterait celle observée à l'ouverture de la page.
 */
const QUEUE_POLL_MS = 5000;

/**
 * Explique une attente en file d'attente.
 *
 * L'écran affichait « En file d'attente… 0 % » sans rien d'autre, parfois pendant
 * plusieurs minutes : aucune progression, aucun log, aucun motif. Or les deux
 * situations qui produisent cet affichage n'appellent pas la même réaction —
 * attendre son tour derrière un autre déploiement, ou signaler que le service de
 * construction ne consomme plus la file.
 */
function QueueNotice({ queue }: { queue: DeploymentQueueStatusDto }) {
  const bloque = queue.workers === 0;

  const message = bloque
    ? 'Aucun service de construction n’est connecté à la file : ce déploiement ne ' +
      'démarrera pas tant qu’il n’aura pas été relancé. Signalez-le au service informatique.'
    : queue.ahead > 0
      ? `${queue.ahead} déploiement${queue.ahead > 1 ? 's' : ''} ${
          queue.ahead > 1 ? 'passent' : 'passe'
        } avant celui-ci.`
      : queue.active > 0
        ? 'Un autre déploiement occupe le service de construction ; celui-ci démarrera juste après.'
        : 'Prise en charge imminente.';

  return (
    <p
      role="status"
      className={
        bloque
          ? 'mt-4 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900 dark:border-rose-900 dark:bg-rose-950/50 dark:text-rose-200'
          : 'mt-4 text-sm text-slate-500 dark:text-slate-400'
      }
    >
      {message}
    </p>
  );
}

/** Convertit une ligne persistée en entrée de tampon. */
function toLogEntry(entry: {
  step: LogEntry['step'];
  level: string;
  line: string;
  createdAt: string;
}): LogEntry {
  return {
    seq: null,
    step: entry.step,
    level: entry.level as LogEntry['level'],
    line: entry.line,
    at: entry.createdAt,
  };
}

/**
 * Charge les `max` dernières lignes de log persistées, en récupérant les
 * dernières pages plutôt que les premières : c'est la fin du build qui
 * explique l'échec, pas son début.
 */
async function loadTailLogs(deploymentId: string, max: number): Promise<LogEntry[]> {
  const first = await getDeploymentLogs(deploymentId, 1, LOG_PAGE_SIZE);
  const lastPage = Math.max(1, Math.ceil(first.total / LOG_PAGE_SIZE));
  const pagesToFetch = Math.min(lastPage, Math.ceil(max / LOG_PAGE_SIZE));
  const startPage = lastPage - pagesToFetch + 1;

  const pages = await Promise.all(
    Array.from({ length: pagesToFetch }, (_unused, index) =>
      startPage + index === 1
        ? Promise.resolve(first)
        : getDeploymentLogs(deploymentId, startPage + index, LOG_PAGE_SIZE),
    ),
  );

  const entries = pages.flatMap((page) => page.items).map(toLogEntry);
  return entries.slice(Math.max(0, entries.length - max));
}

/** Squelette affiché tant que le premier `snapshot` n'est pas arrivé. */
function ProgressSkeleton() {
  return (
    <Card className="space-y-4 p-6">
      <Skeleton className="h-6 w-1/2" />
      <Skeleton className="h-3 w-full" />
      <div className="grid gap-2 sm:grid-cols-3">
        {Array.from({ length: 6 }, (_unused, index) => (
          <Skeleton key={index} className="h-5 w-full" />
        ))}
      </div>
    </Card>
  );
}

/** Suivi temps réel d'un déploiement. */
export function DeploymentProgressPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSession();

  const stream = useDeploymentStream(id);
  const detail = useAsync(() => getDeployment(id), [id]);

  const [retrying, setRetrying] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [fullLogs, setFullLogs] = useState<LogEntry[] | null>(null);
  const [loadingFullLogs, setLoadingFullLogs] = useState(false);
  /** Décision en cours d'envoi sur les changements annoncés par l'IA. */
  const [reviewing, setReviewing] = useState<'accept' | 'refuse' | 'refine' | null>(null);

  const finished = stream.state !== null && isTerminal(stream.state);
  const awaiting = stream.state !== null && isAwaitingUser(stream.state);

  // Le compteur « en cours depuis » s'arrête dès que le déploiement cesse
  // d'avancer : terminé, ou suspendu en attente d'une décision. Le laisser courir
  // pendant l'attente compterait le temps de réflexion comme du travail.
  const maintenant = useTicker(1000, !finished && !awaiting);

  /**
   * Recharge la fiche à chaque changement de nature de l'état.
   *
   * À la fin du job, l'URL, la durée et le motif d'échec définitifs y sont
   * consolidés. À l'entrée en attente de validation, c'est la liste des
   * changements à présenter : la fiche a été chargée au montage de la page, donc
   * **avant** que la transformation ne l'y écrive. Sans ce rechargement, le
   * panneau de validation resterait vide sur un déploiement qui, lui, ne repartira
   * pas sans réponse.
   */
  useEffect(() => {
    if (finished || awaiting) detail.reload();
    // `detail.reload` est stable (useCallback sans dépendance).
  }, [finished, awaiting]);

  /**
   * Réinitialise les états d'action au changement de job.
   *
   * Une reprise navigue vers `/deploiements/<nouvel id>` : la route est la même,
   * React garde donc le composant monté et `retrying` restait vrai
   * indéfiniment. Le bouton « Réessayer » demeurait désactivé et son indicateur
   * tournait, y compris après l'échec du nouveau job.
   */
  useEffect(() => {
    setRetrying(false);
    setCancelling(false);
    setReviewing(null);
    setActionError(null);
  }, [id]);

  const state = stream.state ?? detail.data?.state ?? null;

  // Tant que le job attend son tour, rien ne parvient par le flux : la fiche est
  // la seule source d'information, et elle doit donc être redemandée.
  useEffect(() => {
    if (state !== 'QUEUED') return undefined;
    const timer = setInterval(() => detail.reload(), QUEUE_POLL_MS);
    return () => clearInterval(timer);
    // `detail.reload` est stable (useCallback sans dépendance).
  }, [state]);

  const displayedProgress = useAnimatedProgress(stream.progress, state, id);

  const logs = fullLogs ?? stream.logs;

  const slug = detail.data?.slug ?? '';
  const domain = detail.data?.domain ?? '';
  const url = stream.url ?? detail.data?.url ?? null;

  // Nature du job : elle commande le nombre de segments de la barre, le titre
  // de l'écran et le message de succès.
  const type = detail.data?.type ?? 'DEPLOY';
  const rollback = type === 'ROLLBACK';

  const durationMs = useMemo(() => {
    if (detail.data?.durationMs != null) return detail.data.durationMs;
    if (stream.createdAt && stream.finishedAt) {
      return new Date(stream.finishedAt).getTime() - new Date(stream.createdAt).getTime();
    }
    return null;
  }, [detail.data?.durationMs, stream.createdAt, stream.finishedAt]);

  /** Relance le job échoué et bascule sur le nouveau. */
  const onRetry = (): void => {
    setRetrying(true);
    setActionError(null);
    retryDeployment(id)
      .then((response) => navigate(`/deploiements/${response.jobId}`))
      .catch((error: unknown) => {
        setActionError(messageOf(error));
        setRetrying(false);
      });
  };

  /** Annule le job (utilisateur : uniquement en file d'attente — §4.2). */
  const onCancel = (): void => {
    setCancelling(true);
    setActionError(null);
    cancelDeployment(id)
      .then(() => {
        detail.reload();
        // L'annulation est écrite en base sans passer par le canal d'événements :
        // on rouvre le flux pour obtenir un snapshot à jour, sinon l'écran
        // resterait bloqué sur l'état précédent.
        stream.reconnect();
        setCancelling(false);
      })
      .catch((error: unknown) => {
        setActionError(messageOf(error));
        setCancelling(false);
      });
  };

  /**
   * Transmet la décision sur les changements de comportement annoncés (§5.1).
   *
   * Le flux est rouvert dans les deux cas : l'acceptation replace le job en file
   * et le refus l'annule, deux écritures qui ne passent pas par le canal
   * d'événements. Sans cette reconnexion, l'écran resterait sur le panneau de
   * validation alors que la décision est prise.
   */
  const onReview = (accept: boolean): void => {
    setReviewing(accept ? 'accept' : 'refuse');
    setActionError(null);
    reviewDeployment(id, accept)
      .then(() => {
        detail.reload();
        stream.reconnect();
        setReviewing(null);
      })
      .catch((error: unknown) => {
        setActionError(messageOf(error));
        setReviewing(null);
      });
  };

  /**
   * Renvoie des précisions à l'IA, qui reprend sa transformation.
   *
   * Même reconnexion que pour la décision, et pour la même raison : la remise en
   * file ne passe pas par le canal d'événements. L'écran resterait sinon sur le
   * panneau de validation alors que le travail a repris.
   */
  const onRefine = (precisions: string): void => {
    setReviewing('refine');
    setActionError(null);
    refineDeployment(id, precisions)
      .then(() => {
        detail.reload();
        stream.reconnect();
        setReviewing(null);
      })
      .catch((error: unknown) => {
        setActionError(messageOf(error));
        setReviewing(null);
      });
  };

  /**
   * Charge l'intégralité des logs persistés : le flux SSE n'en rejoue que les
   * dernières lignes, ce qui ne suffit pas pour analyser un échec de build.
   */
  const onLoadFullLogs = (): void => {
    setLoadingFullLogs(true);
    setActionError(null);
    loadTailLogs(id, MAX_LOG_LINES)
      .then((entries) => {
        setFullLogs(entries);
        setLoadingFullLogs(false);
      })
      .catch((error: unknown) => {
        setActionError(messageOf(error));
        setLoadingFullLogs(false);
      });
  };

  if (detail.error && state === null) {
    return (
      <>
        <PageHeader title="Déploiement" />
        <ErrorBanner message={detail.error} onRetry={detail.reload} />
      </>
    );
  }

  const cancellable = state !== null && canCancel(user.role, state);

  return (
    <>
      <PageHeader
        title={
          slug
            ? rollback
              ? `Restauration de « ${slug} »`
              : `Déploiement de « ${slug} »`
            : rollback
              ? 'Restauration en cours'
              : 'Déploiement en cours'
        }
        description={
          slug ? (
            <>
              Cible :{' '}
              <span className="font-mono">
                {slug}.{domain}
              </span>
              {typeof detail.data?.versionNumber === 'number' ? (
                <> · Version v{detail.data.versionNumber}</>
              ) : null}
              {rollback ? ' · Restauration sans reconstruction' : null} · Job{' '}
              <span className="font-mono text-xs">{id}</span>
            </>
          ) : (
            <>
              Job <span className="font-mono text-xs">{id}</span>
            </>
          )
        }
        actions={
          <>
            {cancellable ? (
              <Button variant="secondary" onClick={onCancel} loading={cancelling}>
                <Ban className="h-4 w-4" aria-hidden="true" />
                Annuler
              </Button>
            ) : null}
            <Link
              to="/mes-applications"
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Mes applications
            </Link>
          </>
        }
      />

      <div className="space-y-6">
        {stream.connectionError && state !== null && !isTerminal(state) ? (
          <div
            role="status"
            className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200"
          >
            <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
            {stream.connectionError}
            <Button variant="ghost" onClick={stream.reconnect} className="ml-auto">
              Reconnecter maintenant
            </Button>
          </div>
        ) : null}

        {state === null ? (
          <ProgressSkeleton />
        ) : (
          <Card className="p-6">
            <ProgressBar
              state={state}
              progress={state === 'DONE' ? 100 : displayedProgress}
              failedStep={stream.failedStep ?? detail.data?.failedStep ?? null}
              message={stream.message}
              type={type}
            />

            {state === 'QUEUED' && detail.data?.queue ? (
              <QueueNotice queue={detail.data.queue} />
            ) : null}

            <dl className="mt-6 grid gap-x-8 gap-y-3 border-t border-slate-200 pt-4 text-sm sm:grid-cols-3 dark:border-slate-800">
              <div>
                <dt className="text-slate-500 dark:text-slate-400">Démarré</dt>
                <dd className="text-slate-900 dark:text-slate-100">
                  {formatDateTime(stream.createdAt ?? detail.data?.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500 dark:text-slate-400">
                  {finished ? 'Terminé' : awaiting ? 'En attente depuis' : 'En cours depuis'}
                </dt>
                <dd className="text-slate-900 dark:text-slate-100">
                  {finished
                    ? formatDateTime(stream.finishedAt ?? detail.data?.finishedAt)
                    : formatElapsed(stream.createdAt, maintenant.toISOString())}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500 dark:text-slate-400">Durée totale</dt>
                <dd className="text-slate-900 dark:text-slate-100">
                  {finished ? formatDuration(durationMs) : '—'}
                </dd>
              </div>
            </dl>
          </Card>
        )}

        {state === 'AWAITING_REVIEW' && (detail.data?.functionalChanges?.length ?? 0) > 0 ? (
          <FunctionalChangesPanel
            changes={detail.data?.functionalChanges ?? []}
            slug={slug || id}
            versionNumber={detail.data?.versionNumber ?? null}
            /*
              Ce que la liste signifie dépend de ce qu'on déploie. Au-delà de la
              v1, c'est une mise à jour. En v1, une instruction portant une
              description initiale désigne une application écrite par Claude —
              il n'y a alors pas d'« avant » ; une instruction qui ne contient que
              des précisions vient d'un artifact collé auquel l'auteur a demandé
              une correction, et l'artifact, lui, avait bien un comportement.
            */
            kind={
              (detail.data?.versionNumber ?? 1) > 1
                ? 'update'
                : hasInitialDescription(detail.data?.modificationInstruction)
                  ? 'creation'
                  : 'conversion'
            }
            // Une application déjà en ligne le reste : l'enjeu d'un abandon n'est
            // pas le même selon qu'on remplace un service en usage ou qu'on en
            // crée un.
            live={detail.data?.appId !== null && (detail.data?.versionNumber ?? 1) > 1}
            busy={reviewing}
            error={actionError}
            onAccept={() => onReview(true)}
            onRefuse={() => onReview(false)}
            onRefine={onRefine}
            refinements={countRefinements(detail.data?.modificationInstruction)}
          />
        ) : null}

        {/* Attente de validation sans liste exploitable : la fiche n'est pas
            encore arrivée, ou la colonne est vide. Ne rien afficher laisserait un
            écran muet sur un déploiement qui ne repartira pas seul. */}
        {state === 'AWAITING_REVIEW' && (detail.data?.functionalChanges?.length ?? 0) === 0 ? (
          <Card className="p-6">
            <p className="text-sm text-slate-700 dark:text-slate-300">
              {detail.loading
                ? 'Chargement des changements à valider…'
                : 'Ce déploiement attend une validation, mais la liste des changements n’a pas pu être chargée. Rechargez la page ; si le problème persiste, signalez-le au service informatique.'}
            </p>
          </Card>
        ) : null}

        {state === 'DONE' && url ? (
          <SuccessPanel
            url={url}
            slug={slug || id}
            durationMs={durationMs}
            type={type}
            versionNumber={detail.data?.versionNumber ?? null}
          />
        ) : null}

        {state === 'DONE' && !url ? (
          <Card className="p-6">
            <p className="text-sm text-slate-700 dark:text-slate-300">
              {rollback ? 'La restauration est terminée' : 'Le déploiement est terminé'} mais l'URL
              publique n'a pas encore été enregistrée. Rechargez la page dans quelques secondes.
            </p>
          </Card>
        ) : null}

        {state === 'FAILED' || state === 'CANCELLED' ? (
          <FailurePanel
            deploymentId={id}
            slug={slug || '—'}
            domain={domain || '—'}
            url={url}
            state={state}
            failedStep={stream.failedStep ?? detail.data?.failedStep ?? null}
            failureReason={stream.failureReason ?? detail.data?.failureReason ?? null}
            createdAt={stream.createdAt ?? detail.data?.createdAt ?? null}
            finishedAt={stream.finishedAt ?? detail.data?.finishedAt ?? null}
            durationMs={durationMs}
            logs={logs}
            onRetry={onRetry}
            retrying={retrying}
            retryError={actionError}
          />
        ) : null}

        {actionError && state !== 'FAILED' && state !== 'CANCELLED' ? (
          <ErrorBanner message={actionError} />
        ) : null}

        <LogPanel
          logs={logs}
          defaultOpen={state === 'FAILED' || state === 'CANCELLED'}
          actions={
            fullLogs === null ? (
              <Button variant="ghost" onClick={onLoadFullLogs} loading={loadingFullLogs}>
                Charger tous les logs
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setFullLogs(null)}>
                Revenir au flux direct
              </Button>
            )
          }
        />

        {url && state !== 'DONE' ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Adresse prévue :{' '}
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-mono text-indigo-700 underline underline-offset-2 dark:text-indigo-300"
            >
              {url}
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </p>
        ) : null}

        {state !== null && !isTerminal(state) && !isAwaitingUser(state) ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Vous pouvez fermer cette page : {rollback ? 'la restauration' : 'le déploiement'} se
            poursuit et vous retrouverez son avancement en revenant sur cette adresse. Étape en
            cours : {STATE_LABELS[state]}
          </p>
        ) : null}

        {/* Le message inverse, et il compte : rien n'avancera sans décision. Le
            promettre autonome ferait attendre indéfiniment. */}
        {state !== null && isAwaitingUser(state) ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Vous pouvez fermer cette page et revenir plus tard : ce déploiement restera en attente
            de votre décision, et vous le retrouverez depuis «&nbsp;Mes applications&nbsp;». Rien ne
            se poursuivra d'ici là.
          </p>
        ) : null}
      </div>
    </>
  );
}
